import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TypeToConfirm } from "@/components/ui/type-to-confirm";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Trash2Icon } from "lucide-react";
import { CopyField } from "./CopyField";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FramePanel,
} from "@/components/ui/frame";
import { useState } from "react";
import { useAuth } from "@clerk/react";
import { useNavigate } from "react-router-dom";
import { useCampaignStore, useCreatorsStore } from "@/stores";
import { usePaywallComplete } from "@/hooks/onboarding";

const VITE_ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT;

export function TechnicalSection() {
  const navigate = useNavigate();
  const {
    campaign: c,
    campaignId,
    deleteCampaign,
    deleteLoading,
  } = useCampaignStore();
  const { creators } = useCreatorsStore();
  const { getToken } = useAuth();
  const { planName, isLoaded } = usePaywallComplete();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");

  // Derived values
  const canDeleteCampaign =
    (!isLoaded || planName?.toLowerCase().includes("free")) &&
    VITE_ENVIRONMENT === "production";

  const hasActiveCreators = creators.some(
    (creator) => creator.status !== "pending" && creator.status !== "to-contact"
  );

  const canConfirmDelete = hasActiveCreators
    ? deleteConfirmName === c?.campaign_name
    : true;

  // Handlers
  const handleDeleteCampaign = async () => {
    const token = await getToken();
    if (!token || !campaignId) return console.error("No token found");
    await deleteCampaign(campaignId, token);
    navigate("/dashboard/campaigns");
  };

  const handleDialogClose = (open: boolean) => {
    setDeleteDialogOpen(open);
    if (!open) {
      setDeleteConfirmName("");
    }
  };

  return (
    <div className="space-y-6">
      <Frame>
        <FrameHeader>
          <FrameTitle>Technical Details</FrameTitle>
        </FrameHeader>
        <FramePanel>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
            <CopyField label="Campaign ID" value={c?._id} id="User ID" />
            <CopyField label="User ID" value={c?.user_id} id="User ID" />
            <CopyField label="Search ID" value={c?.search_id} id="Search ID" />
            <CopyField label="Organization ID" value={c?.organization_id} id="Organization ID" />
          </div>
        </FramePanel>
      </Frame>

      <Frame>
        <FrameHeader>
          <FrameTitle>Delete Campaign</FrameTitle>
        </FrameHeader>
        <FramePanel>
          <div className="flex flex-wrap justify-between items-center">
            <AlertDialog
              open={deleteDialogOpen}
              onOpenChange={handleDialogClose}
            >
              <AlertDialogTrigger disabled={canDeleteCampaign}>
                <Tooltip>
                  <TooltipTrigger>
                    <Button variant="destructive" disabled={canDeleteCampaign}>
                      <Trash2Icon />
                      Delete Campaign
                    </Button>
                  </TooltipTrigger>
                  {isLoaded && planName?.toLowerCase().includes("free") && (
                    <TooltipContent>
                      Free plans can't delete campaigns. Upgrade to get more
                      campaigns.
                    </TooltipContent>
                  )}
                </Tooltip>
              </AlertDialogTrigger>
              <AlertDialogContent className="max-w-xl">
                <AlertDialogHeader variant="destructive">
                  <AlertDialogTitle>
                    Are you absolutely sure?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    this campaign and remove it's data from our servers.
                  </AlertDialogDescription>
                </AlertDialogHeader>

                {hasActiveCreators && (
                  <TypeToConfirm
                    phrase={c?.campaign_name || ""}
                    value={deleteConfirmName}
                    onValueChange={setDeleteConfirmName}
                    onEnter={handleDeleteCampaign}
                    disabled={deleteLoading}
                  />
                )}

                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
                  <Button
                    variant="destructive"
                    disabled={deleteLoading || !canConfirmDelete}
                    onClick={handleDeleteCampaign}
                  >
                    {deleteLoading ? (
                      <Spinner />
                    ) : (
                      <Trash2Icon />
                    )}
                    Delete Campaign
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <div className="flex flex-col text-sm">
              <span className="text-muted-foreground text-xs">Creation Date</span>
              <span>{c?.creation_date}</span>
            </div>
          </div>
        </FramePanel>
      </Frame>
    </div>
  );
}
